import { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Box,
  styled,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { theme } from '~/shared/styles/theme';
import { SENTRY_BREADCRUMBS, SENTRY_OPERATIONS } from '../constants';
import { reportError, addBreadcrumb } from '../utils/sentry';
import Toast from './Toast';
import useToast from '../hooks/useToast';
import {
  UPDATE_MDI_ONBOARDING_STARTED,
  UPDATE_MDI_ONBOARDING_COMPLETED,
} from '~/graphql/mutations';
import { ME } from '~/graphql/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  url: string | null;
}

const MdiOnboardingModal = ({ open, onClose, url }: Props) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const { toast, showToast, hideToast } = useToast();
  const [updateMdiOnboardingStarted] = useMutation(
    UPDATE_MDI_ONBOARDING_STARTED,
    {
      refetchQueries: [{ query: ME }],
      awaitRefetchQueries: true,
    },
  );
  const [updateMdiOnboardingCompleted] = useMutation(
    UPDATE_MDI_ONBOARDING_COMPLETED,
    {
      refetchQueries: [{ query: ME }],
      awaitRefetchQueries: true,
    },
  );

  // Reset loading state when URL changes
  useEffect(() => {
    if (url) {
      setIsIframeLoading(true);
    }
  }, [url]);

  useEffect(() => {
    if (!open || !url) {
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      try {
        const iframeOrigin = new URL(url).origin;

        if (event.origin === iframeOrigin) {
          if (event.data?.event === 'load') {
            try {
              await updateMdiOnboardingStarted();
            } catch (error) {
              // noop -- don't block user if we can't update timestamp
              reportError(error, {
                operation: SENTRY_OPERATIONS.MDI_UPDATE_ONBOARDING_STARTED,
              });
            }
          }

          if (event.data?.event === 'finish') {
            try {
              await updateMdiOnboardingCompleted();
            } catch (error) {
              // noop -- don't block user if we can't update timestamp
              reportError(error, {
                operation: SENTRY_OPERATIONS.MDI_UPDATE_ONBOARDING_COMPLETED,
              });
            }

            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }

            // Set a 5-second timeout to close the modal
            closeTimeoutRef.current = setTimeout(() => {
              onClose();
            }, 5000);
          }
        }
      } catch (error) {
        // Handle URL parsing errors (invalid URL from backend)
        // new URL() throws TypeError when URL is invalid
        if (error instanceof TypeError) {
          showToast('Invalid MDI onboarding URL. Please try again.', 'error');
          return;
        }

        // Log unexpected errors
        addBreadcrumb(
          'MDI iframe postMessage error',
          SENTRY_BREADCRUMBS.MDI_IFRAME,
          {
            eventDataType: typeof event.data,
          },
        );
        reportError(error, {
          operation: SENTRY_OPERATIONS.MDI_HANDLE_POST_MESSAGE,
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [
    open,
    url,
    onClose,
    showToast,
    updateMdiOnboardingStarted,
    updateMdiOnboardingCompleted,
  ]);

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
  };

  const showLoading = !url || isIframeLoading;

  const fullScreenUrl = `${url}&fullscreen=true`;

  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth>
        <StyledDialogTitle>
          <CloseButton onClick={onClose} size="small">
            Close
          </CloseButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          {showLoading && (
            <LoadingContainer>
              <CircularProgress />
            </LoadingContainer>
          )}
          {url && (
            <StyledIframe
              src={fullScreenUrl}
              title="MDI Onboarding"
              allow="clipboard-read; clipboard-write"
              onLoad={handleIframeLoad}
              style={{ display: isIframeLoading ? 'none' : 'block' }}
            />
          )}
        </StyledDialogContent>
      </StyledDialog>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </>
  );
};

export default MdiOnboardingModal;

const CloseButton = styled(Button)({
  color: theme.palette.custom.textMain,
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '90vw',
    height: '90vh',
    maxWidth: '1260px',
    maxHeight: '90vh',
    borderRadius: '10px',
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'flex-end',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.custom.textMain,
  fontWeight: 600,
  borderBottom: `solid 1px ${theme.palette.custom.lightGrayBrown}`,
  padding: '16px 24px',
});

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  display: 'flex',
  overflow: 'hidden',
  '&:first-of-type': {
    paddingTop: 0,
  },
});

const StyledIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none',
  flexGrow: 1,
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: '400px',
});
